import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { FichaCadastralOrgaoService } from '@/core/api/sicom-api/registro/orgao/relatorios/ficha-cadastral/ficha-cadastral-orgao.service';
import { FichaCadastralOrgaoRequest, FichaCadastralOrgaoResponse } from '@/core/api/sicom-api/registro/orgao/relatorios/ficha-cadastral/ficha-cadastral-orgao.model';
import { FichaCadastralOrgaoJobService } from '@/core/services/ficha-cadastral-orgao-job.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = FichaCadastralOrgaoRequest;
type Resp = FichaCadastralOrgaoResponse;

interface FichaCadastralOrgaoFormControls {
    uf: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono da Ficha Cadastral do órgão/delegacia (1 conselho/delegacia por UF).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a UF
 * (o backend resolve o `idSolicitante` a partir dela, mesma regra do dashboard-orgao).
 */
@Component({
    selector: 'app-relatorio-ficha-cadastral',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, SelectEstado, RelatorioContainer, CanDirective],
    templateUrl: './relatorio-ficha-cadastral.html',
    styleUrl: './relatorio-ficha-cadastral.scss'
})
export class RelatorioFichaCadastral extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.orgaos.relatorios.gerar;

    private readonly relatorioService = inject(FichaCadastralOrgaoService);
    readonly jobService = inject(FichaCadastralOrgaoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    readonly form = new FormGroup<FichaCadastralOrgaoFormControls>({
        uf: new FormControl<string | null>(null, Validators.required)
    });

    get gerarBloqueado(): boolean {
        return this.isLoading() || this.isPending || this.form.invalid;
    }

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir da UF selecionada; retorna null (marcando o campo) se vazia. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.gerarBloqueado) return null;
        return { uf: this.form.getRawValue().uf as string };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    changeUf(uf: string | null): void {
        this.form.get('uf')?.setValue(uf ?? null);
        this.ufTocada = true;
    }

    override marcarUfTocada(): void {
        this.ufTocada = true;
        this.form.get('uf')?.markAsTouched();
    }

    limparFormulario(): void {
        this.ufTocada = false;
        this.form.reset({ uf: null });
        this.limparJob();
    }
}
